import React, { useEffect, useRef } from 'react';
import {
  Sparkles, X, Loader2, AlertCircle, ArrowUpRight,
  MessageSquare, Send, ExternalLink, History, Minimize2,
} from 'lucide-react';
import { getFrequentQueries } from '../queryHistory';
import BotAvatar from './BotAvatar';

const QUICK_PROMPTS = [
  "Summarize this week's high-risk alerts",
  'Which companies have regulatory scrutiny?',
  'Latest legal developments to watch',
];

/**
 * Floating Ask AI bot — bottom-right FAB + chat panel.
 * Uses the same popup ask flow as Header "Ask AI".
 */
function AiBotWidget({
  open,
  onOpen,
  onClose,
  hidden = false,
  bubble = true,
  aiOverview,
  aiOverviewLoading,
  popupQuestion,
  setPopupQuestion,
  popupAnswer,
  popupLoading,
  popupError,
  askInPopup,
  goDeepDive,
}) {
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const frequent = getFrequentQueries(4);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      clearTimeout(t);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [open, popupAnswer, popupLoading, popupError]);

  if (hidden || (!bubble && !open)) return null;

  const suggestions = (aiOverview?.suggestions?.length > 0 ? aiOverview.suggestions : QUICK_PROMPTS).slice(0, 4);

  return (
    <div className="fixed z-[55] bottom-20 right-4 sm:bottom-6 sm:right-6 flex flex-col items-end gap-3 pointer-events-none">
      {/* Chat panel */}
      {open && (
        <div
          className="pointer-events-auto w-[min(100vw-2rem,380px)] h-[min(70vh,560px)] flex flex-col rounded-lg bg-white border border-[#e1e5ec] shadow-xl shadow-slate-900/20 overflow-hidden motion-safe:animate-[drawerContent_200ms_ease-out]"
          role="dialog"
          aria-modal="false"
          aria-label="Quick Assistant"
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-3.5 bg-[#1c4ed8] text-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-md bg-white border border-[#e1e5ec] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <BotAvatar size={32} decorative className="scale-110" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight">Quick Assistant</p>
                  <p className="text-xs text-white/80 truncate">Fast answers from your intelligence</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-8 w-8 rounded-lg hover:bg-white flex items-center justify-center transition"
                  aria-label="Minimize chat"
                  title="Minimize"
                >
                  <Minimize2 size={16} />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-8 w-8 rounded-lg hover:bg-white flex items-center justify-center transition"
                  aria-label="Close chat"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3.5 py-3.5 space-y-3 bg-[#f6f7fa]">
            {aiOverviewLoading && (
              <div className="flex items-center gap-2 text-sm text-[#5b6780] py-6 justify-center">
                <Loader2 size={15} className="animate-spin text-[#1c4ed8]" />
                Preparing insights…
              </div>
            )}

            {aiOverview && !aiOverviewLoading && (
              <div className="rounded-md border border-[#c5d3f6] bg-[#e7edfc] p-3">
                <p className="text-xs font-semibold text-[#1c4ed8] mb-1 flex items-center gap-1">
                  <Sparkles size={11} /> Snapshot
                </p>
                <p className="text-xs text-[#45516a] leading-relaxed">{aiOverview.overview}</p>
              </div>
            )}

            {popupLoading && (
              <div className="rounded-md border border-[#e1e5ec] bg-white p-3 flex items-center gap-2 text-sm text-[#5b6780] shadow-sm">
                <Loader2 size={14} className="animate-spin text-[#1c4ed8]" />
                Searching records…
              </div>
            )}

            {popupError && (
              <div className="rounded-md border border-red-200 bg-[#fae8e7] p-3 text-xs text-[#bd2f2b] flex items-start gap-2">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{popupError}</span>
              </div>
            )}

            {popupAnswer && !popupLoading && (
              <div className="rounded-md border border-[#e1e5ec] bg-white p-3.5 shadow-sm">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-6 h-6 rounded-lg bg-[#1c4ed8] flex items-center justify-center flex-shrink-0">
                    <Sparkles size={12} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {popupAnswer.context_used && (
                      <p className="text-xs font-semibold text-[#1c4ed8] mb-1">
                        Context: {popupAnswer.context_used}
                      </p>
                    )}
                    <p className="text-sm text-[#45516a] whitespace-pre-wrap leading-relaxed">
                      {popupAnswer.answer}
                    </p>
                  </div>
                </div>
                {popupAnswer.sources?.length > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-[#e1e5ec] space-y-1">
                    <p className="text-xs font-semibold text-[#76829a] mb-1">Sources</p>
                    {popupAnswer.sources.slice(0, 4).map((s, i) => (
                      <a
                        key={s.id || i}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={s.title}
                        className="flex items-center gap-1.5 text-xs text-[#1c4ed8] hover:text-[#1c4ed8] bg-[#f6f7fa] hover:bg-[#e7edfc] border border-[#e1e5ec] rounded-lg px-2.5 py-1.5 transition"
                      >
                        <ExternalLink size={10} className="flex-shrink-0" />
                        <span className="truncate">{s.title}</span>
                      </a>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => goDeepDive()}
                  className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-[#1c4ed8] hover:text-[#1c4ed8] bg-[#e7edfc] hover:bg-[#e7edfc] px-3 rounded-md transition h-8"
                >
                  <ArrowUpRight size={12} /> Open full AI Assistant
                </button>
              </div>
            )}

            {!popupAnswer && !popupLoading && !aiOverviewLoading && (
              <>
                <div>
                  <p className="text-xs font-semibold text-[#76829a] mb-2">Try asking</p>
                  <div className="space-y-1.5">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setPopupQuestion(s);
                          askInPopup(s);
                        }}
                        className="w-full text-left text-xs text-[#45516a] bg-white hover:bg-[#e7edfc] border border-[#e1e5ec] hover:border-[#c5d3f6] px-3 py-2.5 rounded-md transition flex items-center gap-2 group shadow-sm"
                      >
                        <MessageSquare size={13} className="text-[#76829a] group-hover:text-[#1c4ed8] flex-shrink-0" />
                        <span className="flex-1 line-clamp-2 leading-snug group-hover:text-[#1c4ed8]">{s}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {frequent.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-[#76829a] mb-1.5 flex items-center gap-1">
                      <History size={11} /> Recent
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {frequent.map((q, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setPopupQuestion(q);
                            askInPopup(q);
                          }}
                          className="text-[13px] text-[#45516a] bg-white border border-[#e1e5ec] hover:border-[#c5d3f6] hover:text-[#1c4ed8] px-3 rounded-md transition truncate max-w-full h-8"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              askInPopup();
            }}
            className="flex-shrink-0 border-t border-[#e1e5ec] p-3 bg-white"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={popupQuestion}
                onChange={(e) => setPopupQuestion(e.target.value)}
                placeholder="Ask about risks, companies…"
                className="flex-1 h-10 px-3.5 text-[13px] bg-[#f6f7fa] border border-[#e1e5ec] rounded-md focus:outline-none focus:border-[#1c4ed8] placeholder:text-[#76829a]"
              />
              <button
                type="submit"
                disabled={!popupQuestion.trim() || popupLoading}
                className="h-10 w-10 rounded-md bg-[#1c4ed8] hover:brightness-110 text-white flex items-center justify-center transition disabled:opacity-40 flex-shrink-0 shadow-sm"
                aria-label="Send question"
              >
                {popupLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FAB */}
      {bubble && (
      <button
        type="button"
        onClick={() => (open ? onClose() : onOpen())}
        className={`pointer-events-auto group relative h-14 w-14 rounded-full shadow-lg shadow-slate-900/20 flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 ${
 open
 ? 'bg-[#1c4ed8] hover:brightness-110 text-white'
 : 'bg-[#1c4ed8] hover:brightness-110 text-white'
 }`}
        aria-label={open ? 'Close Quick Assistant' : 'Open Quick Assistant'}
        title={open ? 'Close' : 'Quick Assistant'}
      >
        {!open && (
          <span className="absolute inset-0 rounded-full bg-blue-400/40 animate-ping opacity-40" aria-hidden />
        )}
        {open ? (
          <X size={22} />
        ) : (
          <span className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-white">
            <BotAvatar size={48} decorative className="scale-110" />
          </span>
        )}
        {!open && (
          <span className="absolute -top-1 -left-1 sm:hidden sr-only">Quick Assistant</span>
        )}
      </button>
      )}

      {/* Desktop label tip when closed */}
      {bubble && !open && (
        <span className="pointer-events-none absolute bottom-[4.25rem] right-0 hidden sm:block opacity-0 group-hover:opacity-100" />
      )}
    </div>
  );
}

export default AiBotWidget;
